import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import './Tabs.css';

const TabsContext = createContext();

/**
 * Tabs Component
 *
 * A fully accessible tabs component with keyboard navigation support.
 * Follows WAI-ARIA best practices for tab interfaces.
 *
 * @example
 * <Tabs value="overview" onValueChange={setActiveTab}>
 *   <TabsList>
 *     <TabsTrigger value="overview">Overview</TabsTrigger>
 *     <TabsTrigger value="details">Details</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="overview">Overview content</TabsContent>
 *   <TabsContent value="details">Details content</TabsContent>
 * </Tabs>
 */
export const Tabs = ({ value, onValueChange, children, className = '', defaultValue, ...props }) => {
    const [activeTab, setActiveTab] = useState(value || defaultValue);
    const [tabValues, setTabValues] = useState([]);

    const handleTabChange = (newValue) => {
        setActiveTab(newValue);
        if (onValueChange) {
            onValueChange(newValue);
        }
    };

    // Update activeTab when value prop changes (controlled component)
    useEffect(() => {
        if (value !== undefined) {
            setActiveTab(value);
        }
    }, [value]);

    return (
        <TabsContext.Provider value={{
            activeTab,
            setActiveTab: handleTabChange,
            tabValues,
            setTabValues
        }}>
            <div className={`${className}`} {...props}>
                {children}
            </div>
        </TabsContext.Provider>
    );
};

/**
 * TabsList Component
 *
 * Container for tab triggers with keyboard navigation support.
 * Handles arrow key navigation and home/end keys.
 */
export const TabsList = ({ children, className = '', ...props }) => {
    const { activeTab, setActiveTab, tabValues, setTabValues } = useContext(TabsContext);
    const listRef = useRef(null);

    // Register tab values from children
    useEffect(() => {
        const values = [];
        React.Children.forEach(children, (child) => {
            if (child && child.props && child.props.value) {
                values.push(child.props.value);
            }
        });
        setTabValues(values);
    }, [children, setTabValues]);

    const handleKeyDown = (e) => {
        const currentIndex = tabValues.indexOf(activeTab);
        let nextIndex = currentIndex;

        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                nextIndex = currentIndex > 0 ? currentIndex - 1 : tabValues.length - 1;
                break;
            case 'ArrowRight':
                e.preventDefault();
                nextIndex = currentIndex < tabValues.length - 1 ? currentIndex + 1 : 0;
                break;
            case 'Home':
                e.preventDefault();
                nextIndex = 0;
                break;
            case 'End':
                e.preventDefault();
                nextIndex = tabValues.length - 1;
                break;
            default:
                return;
        }

        if (nextIndex !== currentIndex && tabValues[nextIndex]) {
            setActiveTab(tabValues[nextIndex]);
            // Focus the newly activated tab
            const buttons = listRef.current?.querySelectorAll('button[role="tab"]');
            if (buttons && buttons[nextIndex]) {
                buttons[nextIndex].focus();
            }
        }
    };

    const defaultClassName = 'tab-navigation';
    return (
        <div
            ref={listRef}
            className={className || defaultClassName}
            role="tablist"
            onKeyDown={handleKeyDown}
            {...props}
        >
            {children}
        </div>
    );
};

/**
 * TabsTrigger Component
 *
 * Individual tab button with full accessibility support.
 */
export const TabsTrigger = ({ value, children, className = '', disabled = false, ...props }) => {
    const { activeTab, setActiveTab } = useContext(TabsContext);
    const isActive = activeTab === value;

    const defaultClassName = `tab-button ${isActive ? 'active' : ''}`;

    return (
        <button
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${value}`}
            id={`tab-${value}`}
            tabIndex={isActive ? 0 : -1}
            data-state={isActive ? 'active' : 'inactive'}
            className={className || defaultClassName}
            onClick={() => setActiveTab(value)}
            disabled={disabled}
            {...props}
        >
            {children}
        </button>
    );
};

/**
 * TabsContent Component
 *
 * Content panel for a tab with accessibility support.
 */
export const TabsContent = ({ value, children, className = '', ...props }) => {
    const { activeTab } = useContext(TabsContext);

    if (activeTab !== value) {
        return null;
    }

    return (
        <div
            role="tabpanel"
            id={`tabpanel-${value}`}
            aria-labelledby={`tab-${value}`}
            tabIndex={0}
            className={`tab-content ${className}`}
            {...props}
        >
            {children}
        </div>
    );
};
